import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { RedirectCondition } from "./ConditionalRedirectSettings";

interface FormField {
  label: string;
  type: string;
  required: boolean;
}

interface FormPreviewProps {
  settings: {
    title: string;
    submitText: string;
    redirectUrl?: string;
    fields?: FormField[];
    enableConditionalRedirects?: boolean;
    conditionalRedirects?: RedirectCondition[];
    productPrice?: number;
  };
}

export const FormPreview = ({ settings }: FormPreviewProps) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const handleInputChange = (fieldLabel: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldLabel]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // التحقق من الحقول المطلوبة
    const requiredFields = settings.fields?.filter(f => f.required) || [];
    const missingFields = requiredFields.filter(f => !formData[f.label]);

    if (missingFields.length > 0) {
      toast.error(`يرجى ملء جميع الحقول المطلوبة: ${missingFields.map(f => f.label).join(', ')}`);
      setIsSubmitting(false);
      return;
    }

    // محاكاة إرسال البيانات
    // await new Promise(resolve => setTimeout(resolve, 1500));

    try {
      const landingPageId = new URLSearchParams(window.location.search).get("page_id");
      // Also try to get ID from path if not in query params (e.g. /funnel/:id)
      const pathId = window.location.pathname.split('/').pop();
      const finalPageId = landingPageId || pathId;

      if (finalPageId) {
        const res = await fetch('/api/landing_page_submissions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            landing_page_id: finalPageId,
            form_data: {
              ...formData,
              quantity
            }
          })
        });
        const json = await res.json();

        if (json.error) {
          console.error("Error saving submission:", json.error);
          toast.error("حدث خطأ أثناء حفظ الطلب");
          setIsSubmitting(false);
          return;
        }
      }

      console.log('Form submitted:', formData);
      setIsSuccess(true);
      toast.success('تم إرسال النموذج بنجاح!');

      // حساب الصفحة المستهدفة بناءً على الشروط
      let targetUrl = settings.redirectUrl || '/thank-you';

      if (settings.enableConditionalRedirects && settings.conditionalRedirects && settings.conditionalRedirects.length > 0) {
        const currentQuantity = quantity;
        const productPrice = settings.productPrice || 0;
        const totalAmount = currentQuantity * productPrice;

        // البحث عن أول شرط متطابق
        for (const condition of settings.conditionalRedirects) {
          const testValue = condition.type === 'quantity' ? currentQuantity : totalAmount;
          let matches = false;

          switch (condition.operator) {
            case 'equal':
              matches = testValue === condition.value;
              break;
            case 'greater':
              matches = testValue > condition.value;
              break;
            case 'less':
              matches = testValue < condition.value;
              break;
            case 'greaterOrEqual':
              matches = testValue >= condition.value;
              break;
            case 'lessOrEqual':
              matches = testValue <= condition.value;
              break;
          }

          if (matches) {
            targetUrl = condition.redirectUrl;
            console.log(`Condition matched: ${condition.type} ${condition.operator} ${condition.value}, redirecting to: ${targetUrl}`);
            break;
          }
        }
      }

      if (targetUrl) {
        setTimeout(() => {
          window.location.href = targetUrl;
        }, 2000);
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("فشل إرسال النموذج");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg animate-scale-in">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center animate-bounce">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold">تم الإرسال بنجاح!</h3>
          <p className="text-muted-foreground">سنتواصل معك قريباً</p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg animate-fade-in"
    >
      <h3 className="text-2xl font-bold mb-6 text-center">{settings.title}</h3>

      <div className="space-y-4">
        {settings.fields?.map((field, idx) => (
          <div key={idx} className="space-y-2">
            <Label htmlFor={`field-${idx}`}>
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </Label>

            {field.type === "textarea" ? (
              <Textarea
                id={`field-${idx}`}
                value={formData[field.label] || ""}
                onChange={(e) => handleInputChange(field.label, e.target.value)}
                required={field.required}
                rows={3}
                className="transition-all focus:scale-[1.02]"
              />
            ) : (
              <Input
                id={`field-${idx}`}
                type={field.type}
                value={formData[field.label] || ""}
                onChange={(e) => handleInputChange(field.label, e.target.value)}
                required={field.required}
                className="transition-all focus:scale-[1.02]"
              />
            )}
          </div>
        ))}

        {/* Quantity selector for conditional redirects */}
        {settings.enableConditionalRedirects && (
          <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
            <Label>الكمية</Label>
            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 h-10 rounded-lg bg-secondary hover:bg-secondary/80 flex items-center justify-center font-bold text-lg transition-all hover:scale-110"
              >
                -
              </button>
              <span className="text-2xl font-bold min-w-[3rem] text-center">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 h-10 rounded-lg bg-secondary hover:bg-secondary/80 flex items-center justify-center font-bold text-lg transition-all hover:scale-110"
              >
                +
              </button>
            </div>
          </div>
        )}
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full mt-6 font-bold text-lg py-6 transition-all hover:scale-[1.02] active:scale-95"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-5 w-5 ml-2 animate-spin" />
            جاري الإرسال...
          </>
        ) : (
          settings.submitText
        )}
      </Button>

      <p className="text-xs text-center text-muted-foreground mt-4">
        بياناتك محمية ولن يتم مشاركتها مع أي طرف ثالث
      </p>
    </form>
  );
};
